import { useState, useEffect } from "react";
import { ActivityItem, fmtDate } from "./UI";
import {
  watchDeal,
  updateDeal,
  createTask,
  watchTasksForDeal,
  logDealActivity,
} from "../lib/db";
import { watchDealMergedTimeline } from "../lib/platformActivity";
import { DEAL_STAGES, DEAL_STAGE_MAP, normalizeDealStage } from "../lib/constants";

const TABS = ["Overview", "Timeline", "Tasks"];

export default function DealPanel({ dealId, open, onClose, onOpenContact, contacts = [] }) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [deal, setDeal] = useState(null);
  const [activity, setActivity] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [note, setNote] = useState("");
  const [newTask, setNewTask] = useState("");
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!open || !dealId) {
      setDeal(null);
      return;
    }
    return watchDeal(dealId, setDeal);
  }, [open, dealId]);

  const contactUserId = deal?.contactId || null;

  useEffect(() => {
    if (!open || !dealId) {
      setActivity([]);
      return;
    }
    return watchDealMergedTimeline(dealId, contactUserId, setActivity);
  }, [open, dealId, contactUserId]);

  useEffect(() => {
    if (!open || !dealId) {
      setTasks([]);
      return;
    }
    return watchTasksForDeal(dealId, setTasks);
  }, [open, dealId]);

  useEffect(() => {
    if (!open) setActiveTab("Overview");
  }, [open]);

  useEffect(() => {
    if (!deal) return;
    setDraft({
      title: deal.title || "",
      value: deal.value ?? 0,
      currency: deal.currency || "usd",
      stage: normalizeDealStage(deal.stage),
      notes: deal.notes || "",
      contactId: deal.contactId || "",
      companyName: deal.companyName || "",
      pocName: deal.pocName || "",
      website: deal.website || "",
    });
  }, [deal?.id, open]);

  if (!dealId) return null;

  async function saveOverview() {
    if (!draft || !deal) return;
    await updateDeal(deal.id, {
      title: draft.title.trim(),
      value: Number(draft.value) || 0,
      currency: draft.currency || "usd",
      stage: draft.stage,
      notes: draft.notes,
      contactId: draft.contactId || null,
      contactName: contacts.find((c) => c.id === draft.contactId)?.name || "",
      companyName: (draft.companyName || "").trim(),
      pocName: (draft.pocName || "").trim(),
      website: (draft.website || "").trim(),
    });
    await logDealActivity(deal.id, "deal_updated", "Deal details updated", {});
  }

  async function addTimelineNote() {
    if (!note.trim() || !deal) return;
    await logDealActivity(deal.id, "note_added", note.trim(), { source: "crm" });
    setNote("");
  }

  async function addTask() {
    if (!newTask.trim() || !deal) return;
    await createTask({ title: newTask.trim(), dealId: deal.id, priority: "medium" });
    setNewTask("");
  }

  const linked = deal?.contactId ? contacts.find((c) => c.id === deal.contactId) : null;

  return (
    <div className={`slide-panel ${open ? "open" : ""}`}>
      <div className="panel-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 16 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: "var(--purple-dim)",
              color: "var(--purple)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            $
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{deal?.title || "Deal"}</div>
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              {deal
                ? `${deal.currency?.toUpperCase() || "USD"} ${Number(deal.value || 0).toLocaleString()} · ${
                    DEAL_STAGE_MAP[normalizeDealStage(deal.stage)]?.label || deal.stage
                  }`
                : "…"}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} style={{ fontSize: 18 }}>
            ×
          </button>
        </div>
        <div className="panel-tabs panel-tabs--scroll">
          {TABS.map((t) => (
            <div
              key={t}
              className={`panel-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
              role="tab"
            >
              {t}
            </div>
          ))}
        </div>
      </div>

      <div className="panel-body">
        {activeTab === "Overview" && draft && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Title
              <input
                className="input"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <div className="grid-2-col" style={{ gap: 10 }}>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Value
                <input
                  className="input"
                  type="number"
                  value={draft.value}
                  onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Currency
                <select
                  className="input"
                  value={draft.currency}
                  onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                  <option value="gbp">GBP</option>
                </select>
              </label>
            </div>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Stage
              <select
                className="input"
                value={draft.stage}
                onChange={(e) => setDraft((d) => ({ ...d, stage: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              >
                {DEAL_STAGES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Linked contact
              <select
                className="input"
                value={draft.contactId}
                onChange={(e) => setDraft((d) => ({ ...d, contactId: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              >
                <option value="">— None —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            </label>
            {linked && onOpenContact && (
              <button type="button" className="btn btn-sm" onClick={() => onOpenContact(linked)}>
                Open contact profile
              </button>
            )}
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Company name
              <input
                className="input"
                value={draft.companyName}
                onChange={(e) => setDraft((d) => ({ ...d, companyName: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              POC name
              <input
                className="input"
                value={draft.pocName}
                onChange={(e) => setDraft((d) => ({ ...d, pocName: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Website
              <input
                className="input"
                type="url"
                value={draft.website}
                onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                style={{ marginTop: 4, width: "100%" }}
              />
            </label>
            <label style={{ fontSize: 12, color: "var(--text-2)" }}>
              Notes
              <textarea
                className="textarea"
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                style={{ marginTop: 4, minHeight: 80 }}
              />
            </label>
            <button type="button" className="btn btn-primary btn-sm" onClick={saveOverview}>
              Save deal
            </button>
          </div>
        )}

        {activeTab === "Timeline" && (
          <div>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-3)",
                marginBottom: 12,
                padding: "10px 12px",
                background: "var(--bg-elevated)",
                borderRadius: 8,
                border: "0.5px solid var(--border)",
                lineHeight: 1.45,
              }}
            >
              Merges <strong>deal activity</strong> with the <strong>linked contact’s</strong> full timeline (CRM +
              product + <code style={{ fontSize: 11 }}>email_activity</code>) when a contact is linked.
            </div>
            <div style={{ marginBottom: 14 }}>
              <textarea
                className="textarea"
                placeholder="Add a note on this deal…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ minHeight: 56, marginBottom: 8 }}
              />
              <button type="button" className="btn btn-sm btn-primary" onClick={addTimelineNote}>
                Add to timeline
              </button>
            </div>
            {activity.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--text-3)", padding: 24 }}>No timeline events yet.</div>
            ) : (
              activity.map((ev) => <ActivityItem key={ev.id} event={ev} />)
            )}
          </div>
        )}

        {activeTab === "Tasks" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <input
                className="input"
                placeholder="New task…"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTask()}
                style={{ flex: 1, minWidth: 140, marginBottom: 0 }}
              />
              <button type="button" className="btn btn-sm btn-primary" onClick={addTask}>
                + Add
              </button>
            </div>
            {tasks.length === 0 ? (
              <div style={{ color: "var(--text-3)", textAlign: "center", padding: 24 }}>No tasks</div>
            ) : (
              tasks.map((t) => (
                <div
                  key={t.id}
                  style={{
                    padding: "10px 0",
                    borderBottom: "0.5px solid var(--border)",
                    fontSize: 13,
                    textDecoration: t.status === "completed" ? "line-through" : "none",
                    color: t.status === "completed" ? "var(--text-3)" : "var(--text-1)",
                  }}
                >
                  {t.title}
                  {t.dueAt ? (
                    <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 8 }}>Due {fmtDate(t.dueAt)}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
